import { SyntheticEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useController,
  useFormContext,
  UseControllerProps,
  Path,
} from 'react-hook-form';
import { transformValidationOptions } from '../../../helpers/shared/transformValidationOptions';

export interface UseCheckboxFieldControllerProps<TFieldValues>
  extends Omit<UseControllerProps<TFieldValues>, 'control'> {
  groupName?: Path<TFieldValues>;
  value?: string | number | boolean;
  multiple?: boolean;
  onChange?: (event: SyntheticEvent, checked: boolean) => void;
}

export function useCheckboxFieldController<TFieldValues>(
  props: UseCheckboxFieldControllerProps<TFieldValues>,
) {
  const {
    value,
    multiple,
    onChange,
    rules,
    groupName,
    ...restControllerProps
  } = props;

  const { t } = useTranslation();
  const { control, trigger } = useFormContext<TFieldValues>();

  const checkboxFieldRules = transformValidationOptions<TFieldValues>(t, rules);
  const required = typeof checkboxFieldRules.required !== 'undefined';

  const controllerProps = useController<TFieldValues>({
    control,
    rules: checkboxFieldRules,
    ...restControllerProps,
  });
  const { field } = controllerProps;

  const handleMultipleChange = useCallback(
    (event: { target: EventTarget }, checked: boolean) => {
      const multipleValues = (field.value as string[]) || [];
      const targetValue = (event.target as HTMLInputElement).value;

      const updatedValues = checked
        ? [...multipleValues, targetValue]
        : multipleValues.filter(
            (item: UseCheckboxFieldControllerProps<TFieldValues>['value']) =>
              item && item.toString() !== targetValue.toString(),
          );

      field.onChange(updatedValues);
    },
    [field],
  );

  const handleChange = useCallback(
    (event: SyntheticEvent, checked: boolean) => {
      if (multiple) {
        handleMultipleChange(event, checked);
      } else {
        field.onChange(event, checked);
      }

      if (onChange) {
        onChange(event, checked);
      }

      if (groupName) {
        trigger(groupName);
      }
    },
    [field, multiple, handleMultipleChange, onChange, trigger, groupName],
  );

  const isChecked = multiple
    ? ((field.value as string[]) || []).includes(value?.toString() as string)
    : !!field.value;

  return {
    required,
    isChecked,
    onChangeField: handleChange,
    ...controllerProps,
  };
}
